import comp from '../components/barrel';
import U from '../js/utils';
import svg from '../js/svg';

const EP_BATCHES = 'reports/client-list-email/batches';
const EP_REGISTER_LIST = 'reports/client-list-email/register-list';
const EP_REGISTER = 'reports/client-list-email/register';
const EP_GENERATE = 'reports/client-list-email/generate';
const EP_SEND = 'reports/client-list-email/send';
const EP_CLEAR_ERRORS = 'reports/client-list-email/clear-errors';
const EP_TOGGLE_INCLUDE = 'reports/client-list-email/toggle-include';
const BATCH_COL_COUNT = 10;
const DETAILS_COL_COUNT = 9;

function isBatchQueued(batch) {
  const status = String(batch?.batch_status ?? '').trim().toUpperCase();
  return status === 'QUEUED';
}

function hasFailedRegisterRows(rows) {
  return (rows || []).some((row) => {
    const status = String(row?.status ?? '').trim().toUpperCase();
    return status === 'FAILED';
  });
}

function hasGeneratedRegisterRows(rows) {
  return (rows || []).some((row) => {
    const status = String(row?.status ?? '').trim().toUpperCase();
    return status === 'GENERATED';
  });
}

function extractRegisterListRows(listData) {
  if (Array.isArray(listData)) {
    return listData;
  }
  if (listData && Array.isArray(listData.data)) {
    return listData.data;
  }
  return [];
}

function shouldShowClearErrors(batch, rows) {
  if (hasFailedRegisterRows(rows)) {
    return true;
  }
  const batchFailed = batch?.failed_count ?? batch?.status_counts?.FAILED;
  return Number(batchFailed) > 0;
}

function fillPersonCell(cell, name, email) {
  const n = name != null && String(name).trim() !== '' ? String(name) : '';
  const e = email != null && String(email).trim() !== '' ? String(email) : '';
  if (!n && !e) {
    cell.textContent = '—';
    return;
  }
  if (n) {
    cell.appendChild(document.createTextNode(n));
  }
  if (n && e) {
    cell.appendChild(document.createElement('br'));
  }
  if (e) {
    cell.appendChild(document.createTextNode(e));
  }
}

function displayOrDash(value) {
  if (value == null || String(value).trim() === '') {
    return '—';
  }
  return String(value);
}

function displayCount(value) {
  if (value == null || value === '') {
    return '0';
  }
  return String(value);
}

function canViewReport(row) {
  const canDownload =
    row.can_download === true || row.can_download === 'true' || row.can_download === 1;
  const reportUrl = row.report_url != null ? String(row.report_url).trim() : '';
  return canDownload && reportUrl !== '';
}

function isIncludeYn(value) {
  return value === true || value === 'true' || value === 1 || value === '1';
}

export default {
  async prepare() {
    this.vars = this.vars || {};
    this.vars.batch_page = this.vars.batch_page || 1;
    this.vars.batch_limit = 50;
    this.vars.details_page = this.vars.details_page || 1;
    this.vars.details_limit = 500;
    this.vars.selected_batch_uuid = this.vars.selected_batch_uuid || '';
    this.vars.selected_batch = this.vars.selected_batch || null;
    this.vars.details_rows = this.vars.details_rows || [];
    this.vars.batch_rows = this.vars.batch_rows || [];
    this.vars.active_tab = this.vars.active_tab || 'register';
  },

  async render() {
    await this.fetchBatches();
    this.applyTabVisibility();
  },

  switchTab(tab) {
    this.vars.active_tab = tab;
    this.applyTabVisibility();
    if (tab === 'register') {
      this.fetchBatches();
    } else if (tab === 'details' && this.vars.selected_batch_uuid) {
      this.fetchRegisterList();
    } else if (tab === 'details') {
      this.vars.details_rows = [];
      this.buildDetailsTableBody([]);
      this.updateDetailsHeading();
      this.updateDetailsActionButtons();
    }
  },

  applyTabVisibility() {
    const isRegister = this.vars.active_tab === 'register';
    const registerPanel = U.id('monthlyClientListRegisterPanel');
    const detailsPanel = U.id('monthlyClientListDetailsPanel');
    const registerTab = U.id('monthlyClientListTabRegister');
    const detailsTab = U.id('monthlyClientListTabDetails');

    if (registerPanel) {
      registerPanel.classList.toggle('hide', !isRegister);
    }
    if (detailsPanel) {
      detailsPanel.classList.toggle('hide', isRegister);
    }
    if (registerTab) {
      registerTab.classList.toggle('active', isRegister);
    }
    if (detailsTab) {
      detailsTab.classList.toggle('active', !isRegister);
    }
    this.updateDetailsActionButtons();
  },

  syncSelectedBatch() {
    const match = (this.vars.batch_rows || []).find(
      (row) => row.batch_uuid === this.vars.selected_batch_uuid
    );
    if (match) {
      this.vars.selected_batch = match;
    }
  },

  updateDetailsActionButtons() {
    const generateBtn = U.id('monthlyClientListGenerateReportsBtn');
    const sendBtn = U.id('monthlyClientListSendReportsBtn');
    const clearErrorsBtn = U.id('monthlyClientListClearErrorsBtn');
    const onDetailsTab =
      this.vars.active_tab === 'details' && this.vars.selected_batch_uuid;
    const batch = this.vars.selected_batch;

    if (generateBtn) {
      generateBtn.classList.toggle('hide', !(onDetailsTab && isBatchQueued(batch)));
    }
    if (sendBtn) {
      sendBtn.classList.toggle(
        'hide',
        !(onDetailsTab && hasGeneratedRegisterRows(this.vars.details_rows))
      );
    }
    if (clearErrorsBtn) {
      clearErrorsBtn.classList.toggle(
        'hide',
        !(onDetailsTab && shouldShowClearErrors(batch, this.vars.details_rows))
      );
    }
  },

  async fetchBatches() {
    const payload = {
      page: parseInt(String(this.vars.batch_page), 10) || 1,
      limit: this.vars.batch_limit || 50,
    };

    try {
      const res = await O.PREP([await O.mPOST(EP_BATCHES, payload)]);
      const listData = res[EP_BATCHES];
      const rows = listData && Array.isArray(listData.data) ? listData.data : [];
      const paginationState = listData && listData.pagination ? listData.pagination : null;
      this.vars.batch_rows = rows;

      if (paginationState && paginationState.current_page != null) {
        this.vars.batch_page = paginationState.current_page;
      }

      this.syncSelectedBatch();
      await this.buildBatchTableBody(rows);

      if (paginationState) {
        await this.buildBatchPaginationButtons(paginationState);
        this.updateBatchPaginationInfo(paginationState);
      }
    } catch (e) {
      /* ignore */
    }
  },

  async viewBatchDetails(batchUuid, batchRow) {
    if (!batchUuid) {
      return;
    }
    this.vars.selected_batch_uuid = batchUuid;
    this.vars.selected_batch = batchRow || null;
    this.vars.details_page = 1;
    this.vars.active_tab = 'details';
    this.applyTabVisibility();
    await this.updateDetailsHeading();
    await this.fetchRegisterList();
  },

  buildRegisterListPayload() {
    return {
      batch_uuid: this.vars.selected_batch_uuid,
      page: parseInt(String(this.vars.details_page), 10) || 1,
      limit: this.vars.details_limit || 500,
    };
  },

  async fetchRegisterList() {
    if (!this.vars.selected_batch_uuid) {
      await this.buildDetailsTableBody([]);
      await this.updateDetailsHeading();
      this.vars.details_rows = [];
      this.updateDetailsActionButtons();
      return;
    }

    try {
      const res = await O.PREP([await O.mPOST(EP_REGISTER_LIST, this.buildRegisterListPayload())]);
      const listData = res[EP_REGISTER_LIST];
      const rows = extractRegisterListRows(listData);
      const paginationState = listData && listData.pagination ? listData.pagination : null;
      this.vars.details_rows = rows;

      if (paginationState && paginationState.current_page != null) {
        this.vars.details_page = paginationState.current_page;
      }

      await this.updateDetailsHeading();
      await this.buildDetailsTableBody(rows);
      this.updateDetailsActionButtons();

      if (paginationState) {
        await this.buildDetailsPaginationButtons(paginationState);
        this.updateDetailsPaginationInfo(paginationState);
      }
    } catch (e) {
      /* ignore */
    }
  },

  async updateDetailsHeading() {
    const heading = U.id('monthlyClientListDetailsHeading');
    if (!heading) {
      return;
    }
    if (!this.vars.selected_batch_uuid) {
      heading.textContent = 'Select a batch from Batch register to view details.';
      return;
    }

    const batch = this.vars.selected_batch || {};
    const createdBy = batch.created_by_name ? String(batch.created_by_name) : '';
    const createdDate = batch.batch_created_date
      ? await U.formatDate(batch.batch_created_date)
      : '';
    const recipientCount =
      batch.recipient_count != null ? String(batch.recipient_count) : '';

    const parts = ['Batch details'];
    if (createdBy) {
      parts.push(`created by ${createdBy}`);
    }
    if (createdDate) {
      parts.push(`on ${createdDate}`);
    }
    if (recipientCount !== '') {
      parts.push(`${recipientCount} recipient${recipientCount === '1' ? '' : 's'}`);
    }
    heading.textContent = parts.length > 1 ? parts.join(' — ') : 'Batch details for the selected batch.';
  },

  async generateReports() {
    if (!this.vars.selected_batch_uuid) {
      comp.toast('No batch selected.');
      return;
    }

    try {
      const res = await O.PREP([
        await O.mPOST(EP_GENERATE, {
          batch_uuid: this.vars.selected_batch_uuid,
          max_per_request: 100,
        }),
      ]);
      const result = res[EP_GENERATE];
      if (result && result.status === 'OK') {
        comp.toast(result.message || 'Report generation started.');
        await this.fetchBatches();
        await this.fetchRegisterList();
      } else {
        comp.errorBox('Error', result?.message || 'Failed to generate reports.');
      }
    } catch (e) {
      comp.errorBox('Error', 'Failed to generate reports. Please try again.');
    }
  },

  async sendReports() {
    if (!this.vars.selected_batch_uuid) {
      comp.toast('No batch selected.');
      return;
    }

    try {
      const res = await O.PREP([
        await O.mPOST(EP_SEND, {
          batch_uuid: this.vars.selected_batch_uuid,
          max_per_request: 100,
        }),
      ]);
      const result = res[EP_SEND];
      if (result && result.status === 'OK') {
        comp.toast(result.message || 'Emails queued for included recipients.');
        await this.fetchBatches();
        await this.fetchRegisterList();
      } else {
        comp.errorBox('Error', result?.message || 'Failed to email reports.');
      }
    } catch (e) {
      comp.errorBox('Error', 'Failed to email reports. Please try again.');
    }
  },

  async clearErrors() {
    if (!this.vars.selected_batch_uuid) {
      comp.toast('No batch selected.');
      return;
    }

    try {
      const res = await O.PREP([
        await O.mPOST(EP_CLEAR_ERRORS, {
          batch_uuid: this.vars.selected_batch_uuid,
        }),
      ]);
      const result = res[EP_CLEAR_ERRORS];
      if (result && result.status === 'OK') {
        comp.toast(result.message || 'Errors cleared.');
        await this.fetchBatches();
        await this.fetchRegisterList();
      } else {
        comp.errorBox('Error', result?.message || 'Failed to clear errors.');
      }
    } catch (e) {
      comp.errorBox('Error', 'Failed to clear errors. Please try again.');
    }
  },

  async toggleInclude(registerUuid, checkbox) {
    if (!registerUuid) {
      return;
    }

    const previous = checkbox.checked;

    try {
      const res = await O.PREP([
        await O.mPOST(EP_TOGGLE_INCLUDE, {
          register_uuid: registerUuid,
        }),
      ]);
      const result = res[EP_TOGGLE_INCLUDE];
      if (result && result.status === 'OK') {
        if (result.data && result.data.include_yn != null) {
          checkbox.checked = isIncludeYn(result.data.include_yn);
        }
        checkbox.title = checkbox.checked
          ? 'Included — click to exclude'
          : 'Excluded — click to include';
      } else {
        checkbox.checked = previous;
        comp.errorBox('Error', result?.message || 'Failed to update include.');
      }
    } catch (e) {
      checkbox.checked = previous;
      comp.errorBox('Error', 'Failed to update include. Please try again.');
    }
  },

  async startNewBatch() {
    try {
      const res = await O.PREP([await O.mPOST(EP_REGISTER, {})]);
      const result = res[EP_REGISTER];
      if (result && result.status === 'OK') {
        comp.toast(result.message || 'New batch started');
        this.vars.batch_page = 1;
        await this.fetchBatches();
      } else {
        comp.errorBox('Error', result?.message || 'Failed to start new batch');
      }
    } catch (e) {
      comp.errorBox('Error', 'Failed to start new batch. Please try again.');
    }
  },

  async buildBatchTableBody(listData) {
    const tbody = U.id('monthlyClientListBatchTableBody');
    if (!tbody) return;
    tbody.innerHTML = '';

    if (!listData.length) {
      const tr = tbody.insertRow();
      const cell = tr.insertCell(0);
      cell.colSpan = BATCH_COL_COUNT;
      cell.textContent = 'No batches found.';
      return;
    }

    for (const row of listData) {
      const tr = tbody.insertRow();

      const viewCell = tr.insertCell(0);
      viewCell.style.textAlign = 'center';
      viewCell.style.padding = '4px 8px';
      viewCell.style.whiteSpace = 'nowrap';

      const viewBtn = document.createElement('button');
      viewBtn.type = 'button';
      viewBtn.className = 'btn btn-outline monthly-client-list-view-btn';
      viewBtn.textContent = 'View details';
      viewBtn.title = 'View batch details';
      viewBtn.addEventListener('click', () => {
        this.viewBatchDetails(row.batch_uuid, row);
      });
      viewCell.appendChild(viewBtn);

      const createdCell = tr.insertCell(1);
      createdCell.textContent = row.batch_created_date
        ? await U.formatDate(row.batch_created_date)
        : '—';

      const updatedCell = tr.insertCell(2);
      updatedCell.textContent = row.batch_updated_date
        ? await U.formatDate(row.batch_updated_date)
        : '—';

      const createdByCell = tr.insertCell(3);
      createdByCell.textContent = displayOrDash(row.created_by_name);

      const recipientsCell = tr.insertCell(4);
      recipientsCell.textContent = displayCount(row.recipient_count);

      const counts = row.status_counts || {};
      tr.insertCell(5).textContent = displayCount(counts.excluded);

      const statusCell = tr.insertCell(6);
      const tag = await comp.statusTag(row.batch_status != null ? String(row.batch_status) : '—');
      statusCell.appendChild(tag);

      tr.insertCell(7).textContent = displayCount(row.included_count);
      tr.insertCell(8).textContent = displayCount(counts.SENT);
      tr.insertCell(9).textContent = displayCount(counts.FAILED);
    }
  },

  async buildDetailsTableBody(listData) {
    const tbody = U.id('monthlyClientListTableBody');
    if (!tbody) return;
    tbody.innerHTML = '';

    if (!listData.length) {
      const tr = tbody.insertRow();
      const cell = tr.insertCell(0);
      cell.colSpan = DETAILS_COL_COUNT;
      cell.textContent = this.vars.selected_batch_uuid
        ? 'No register entries found for this batch.'
        : 'Select a batch from Batch register to view details.';
      return;
    }

    for (const row of listData) {
      const tr = tbody.insertRow();

      const includeCell = tr.insertCell(0);
      includeCell.style.textAlign = 'center';
      includeCell.style.padding = '4px 8px';

      const toggleWrap = await comp.toggle_v2({
        elID: `include_${String(row.register_uuid || '').replace(/-/g, '')}`,
        isChecked: isIncludeYn(row.include_yn),
        labelName: '',
      });
      const labelEl = toggleWrap.querySelector('.tgglLable');
      if (labelEl) {
        labelEl.remove();
      }
      const toggleDiv = toggleWrap.querySelector(`[id$="Div"]`);
      if (toggleDiv) {
        toggleDiv.classList.add('monthly-client-list-include-toggle');
      }
      const checkbox = toggleWrap.querySelector('input[type="checkbox"]');
      if (checkbox && row.register_uuid) {
        checkbox.title = isIncludeYn(row.include_yn) ? 'Included — click to exclude' : 'Excluded — click to include';
        checkbox.addEventListener('change', () => {
          this.toggleInclude(row.register_uuid, checkbox);
        });
      }
      includeCell.appendChild(toggleWrap);

      const targetCell = tr.insertCell(1);
      fillPersonCell(targetCell, row.target_full_name, row.target_email);

      const statusCell = tr.insertCell(2);
      const tag = await comp.statusTag(row.status != null ? String(row.status) : '—');
      statusCell.appendChild(tag);

      const reportCell = tr.insertCell(3);
      const reportContainer = document.createElement('div');
      reportContainer.className = 'flex items-center gap-2 flex-wrap';

      if (row.report_filename) {
        const nameEl = document.createElement('span');
        nameEl.textContent = String(row.report_filename);
        reportContainer.appendChild(nameEl);
      } else if (!canViewReport(row)) {
        reportContainer.textContent = '—';
      }

      if (canViewReport(row)) {
        const viewReportBtn = document.createElement('button');
        viewReportBtn.type = 'button';
        viewReportBtn.className = 'btn-transparent monthly-client-list-report-view-btn p-0';
        viewReportBtn.title = row.report_filename
          ? `View ${row.report_filename}`
          : 'View report';
        const viewIcon = await svg.linkNewPage();
        viewReportBtn.appendChild(viewIcon);
        viewReportBtn.addEventListener('click', () => {
          window.open(String(row.report_url), '_blank', 'noopener,noreferrer');
        });
        reportContainer.appendChild(viewReportBtn);
      }

      reportCell.appendChild(reportContainer);

      const errorCell = tr.insertCell(4);
      errorCell.textContent = displayOrDash(row.error_message);

      const createdByCell = tr.insertCell(5);
      createdByCell.textContent = displayOrDash(row.created_by_name);

      const createdCell = tr.insertCell(6);
      createdCell.textContent = row.created_date ? await U.formatDate(row.created_date) : '—';

      const generatedCell = tr.insertCell(7);
      generatedCell.textContent = row.generated_date ? await U.formatDate(row.generated_date) : '—';

      const sentCell = tr.insertCell(8);
      sentCell.textContent = row.sent_date ? await U.formatDate(row.sent_date) : '—';
    }
  },

  async buildBatchPaginationButtons(paginationState) {
    const wrapper = U.id('monthlyClientListBatchPaginationWrapper');
    if (!wrapper) return;

    const totalPages = Number(paginationState.total_pages) || 0;
    if (totalPages < 2) {
      wrapper.innerHTML = '';
      return;
    }

    await comp.paginationButtons({ totalPages, wrapperID: 'monthlyClientListBatchPaginationWrapper' });
    wrapper.querySelectorAll('.pageButton').forEach((button) => {
      button.addEventListener('click', (e) => {
        this.vars.batch_page = Number(e.currentTarget.getAttribute('value'));
        this.fetchBatches();
      });
    });
  },

  async buildDetailsPaginationButtons(paginationState) {
    const wrapper = U.id('monthlyClientListDetailsPaginationWrapper');
    if (!wrapper) return;

    const totalPages = Number(paginationState.total_pages) || 0;
    if (totalPages < 2) {
      wrapper.innerHTML = '';
      return;
    }

    await comp.paginationButtons({ totalPages, wrapperID: 'monthlyClientListDetailsPaginationWrapper' });
    wrapper.querySelectorAll('.pageButton').forEach((button) => {
      button.addEventListener('click', (e) => {
        this.vars.details_page = Number(e.currentTarget.getAttribute('value'));
        this.fetchRegisterList();
      });
    });
  },

  updateBatchPaginationInfo(paginationState) {
    this.updatePaginationInfo(paginationState, [
      ['monthlyClientListBatchTotalRecordsDisplay', 'monthlyClientListBatchTotalPagesDisplay'],
      ['monthlyClientListBatchTotalRecordsDisplayBottom', 'monthlyClientListBatchTotalPagesDisplayBottom'],
    ]);
  },

  updateDetailsPaginationInfo(paginationState) {
    this.updatePaginationInfo(paginationState, [
      ['monthlyClientListDetailsTotalRecordsDisplay', 'monthlyClientListDetailsTotalPagesDisplay'],
      ['monthlyClientListDetailsTotalRecordsDisplayBottom', 'monthlyClientListDetailsTotalPagesDisplayBottom'],
    ]);
  },

  updatePaginationInfo(paginationState, idPairs) {
    const totalCount =
      paginationState.total_count != null
        ? paginationState.total_count
        : paginationState.total_records != null
          ? paginationState.total_records
          : '-';
    const totalPages = paginationState.total_pages != null ? paginationState.total_pages : '-';

    for (const [recordsId, pagesId] of idPairs) {
      const recordsEl = U.id(recordsId);
      const pagesEl = U.id(pagesId);
      if (recordsEl) {
        recordsEl.textContent = totalCount;
      }
      if (pagesEl) {
        pagesEl.textContent = totalPages;
      }
    }
  },
};
